import { useEffect, useRef } from 'react'
import { useAuth } from '@common/auth'
import { useAbility } from '@common/abilities'
import { apiClient } from '@common/api'
import { pubSubBroker } from '@common/pubSub'

/**
 * Notification polling hook — replaces AngularJS `disputeNotificationsService`.
 *
 * Polls GET /api/disputeNotifications every 30 seconds (matches NOTIFICATION_UPDATE_INTERVAL_SEC
 * from webpack.common.js). Only polls when user has 'ViewNotifications' ability.
 *
 * Publishes count changes via pubSubBroker (matching AngularJS notification modules):
 *   - 'ReviewRequests:UpdateCount' — reviewRequestsCount
 *   - 'PendingChargeoff:UpdateCount' — pendingChargeoffsCount
 *   - 'TotalNotifications:UpdateCount' — sum of both counts
 *
 * Used by AppLayout to update navigation badge counts.
 */

const POLLING_INTERVAL_MS = 30 * 1000

interface NotificationResponse {
    reviewRequestsCount: number
    pendingChargeoffsCount: number
}

export function useNotificationPolling() {
    const { isAuthenticated } = useAuth()
    const { hasAbility, isProfileLoaded } = useAbility()

    // Track previous values to only publish on change
    // (matches AngularJS reviewRequestNotification.js / pendingChargeOffNotification.js pattern)
    const prevReviewCount = useRef<number | null>(null)
    const prevChargeoffCount = useRef<number | null>(null)
    const prevTotalCount = useRef<number | null>(null)

    useEffect(() => {
        if (!isAuthenticated || !isProfileLoaded) return
        if (!hasAbility('ViewNotifications')) return

        let cancelled = false

        const poll = () => {
            if (cancelled) return
            apiClient
                .get<NotificationResponse>('/disputeNotifications')
                .then((result) => {
                    if (cancelled) return

                    const { reviewRequestsCount, pendingChargeoffsCount } = result
                    const total = reviewRequestsCount + pendingChargeoffsCount

                    // Only publish when value changes (matches AngularJS notification modules)
                    if (prevReviewCount.current !== reviewRequestsCount) {
                        pubSubBroker.publish('ReviewRequests:UpdateCount', reviewRequestsCount)
                        prevReviewCount.current = reviewRequestsCount
                    }

                    if (prevChargeoffCount.current !== pendingChargeoffsCount) {
                        pubSubBroker.publish('PendingChargeoff:UpdateCount', pendingChargeoffsCount)
                        prevChargeoffCount.current = pendingChargeoffsCount
                    }

                    if (prevTotalCount.current !== total) {
                        pubSubBroker.publish('TotalNotifications:UpdateCount', total)
                        prevTotalCount.current = total
                    }
                })
                .catch(() => {
                    // Swallow errors — polling failures shouldn't crash the app
                })
        }

        // Poll immediately on start, then every 30 seconds
        poll()
        const intervalId = setInterval(poll, POLLING_INTERVAL_MS)

        return () => {
            cancelled = true
            clearInterval(intervalId)
        }
    }, [isAuthenticated, isProfileLoaded, hasAbility])
}
